import * as React from "react";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { PensionArrangementType } from "../../../../../../../Wrap/Constants/Enums/Domain/PensionArrangementType";
import { TransferType } from "../../../../../../../Wrap/Constants/Enums/Domain/TransferType";
import { Money } from "../../../../../../../Wrap/Core/DataTypes/Money";
import { PensionBase } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/PensionBase";
import { DrawdownModel } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/DrawdownModel";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { Table } from "../../../../../../Shared/Table/Table";
import { PensionHelper } from "../../Helpers/PensionHelper";
import { SectionHeader } from "../../SharedComponents";
import { ChooseAssetsToSellAssets } from "./Store/DrawdownCalculationStoreStatic";

interface ICrystallizationPerContributionProps extends IProcessComponentProps {
    data: DrawdownModel;
    modelData: PensionBase;
}

interface ICrystallizationPerContributionState {}

interface ICrystallisationPerConributionTableRow {
    Contribution?: string;
    CrystallisationAmount?: Money;
    LTA?: number;
    TaxFreeLumpSum?: Money;
}

export class CrystallizationPerContribution extends ProcessComponent<
    ICrystallizationPerContributionProps,
    ICrystallizationPerContributionState
> {
    constructor(props: any) {
        super(props);
        this.state = {};
    }

    private getLTACalculation(payment: number, totalAmount: number, LTA: number): number {
        payment = payment || 0;
        const value = (payment / totalAmount) * LTA;
        return Math.round(Math.pow(10, 2) * value) / Math.pow(10, 2);
    }

    private getPCLSAmount(payment: number, availableLTA: number): number {
        payment = payment || 0;
        const firstValue: number = payment * 0.25;
        const secondValue: number = availableLTA * 0.25;
        return firstValue > secondValue ? secondValue : firstValue;
    }

    private getTableData = () => {
        const { data, modelData } = this.props;
        const LTA = data?.LifetimeAllowance ? data?.LifetimeAllowance?.SlaUsedByProposedCrystallisation : 0;
        const availableLTA =
            data?.LifetimeAllowance && data?.LifetimeAllowance?.AvailablePersonalAllowance
                ? data?.LifetimeAllowance?.AvailablePersonalAllowance?.Amount || 0
                : 0;
        const totalValue =
            PensionHelper.singlePaymentsTotal(modelData) +
            PensionHelper.getTransfersTotal(
                (modelData.PensionTransfers || []).filter(
                    (ele) =>
                        ele.PensionSource == PensionArrangementType.Accumulation &&
                        ele.TransferType == TransferType.Cash
                )
            );
        const contributionRow: ICrystallisationPerConributionTableRow[] = [];
        if (modelData.ClientSinglePayment?.Money?.Amount > 0) {
            const value = PensionHelper.clientSinglePayment(modelData);
            contributionRow.push({
                Contribution: "Individual contribution",
                CrystallisationAmount: value,
                LTA: this.getLTACalculation(value.Amount, totalValue, LTA),
                TaxFreeLumpSum: {
                    Amount: this.getPCLSAmount(value.Amount, availableLTA),
                    Currency: CurrencyCode.GBP,
                },
            });
        }

        if (modelData.EmployerSinglePayment?.Money?.Amount > 0) {
            const value = PensionHelper.employerSinglePayment(modelData);
            contributionRow.push({
                Contribution: "Employer contribution",
                CrystallisationAmount: value,
                LTA: this.getLTACalculation(value.Amount, totalValue, LTA),
                TaxFreeLumpSum: {
                    Amount: this.getPCLSAmount(value.Amount, availableLTA),
                    Currency: CurrencyCode.GBP,
                },
            });
        }

        if (modelData.ThirdPartySinglePayment?.Money?.Amount > 0) {
            const value = PensionHelper.thirdPartySinglePayment(modelData);
            contributionRow.push({
                Contribution: "Third party contribution",
                CrystallisationAmount: value,
                LTA: this.getLTACalculation(value.Amount, totalValue, LTA),
                TaxFreeLumpSum: {
                    Amount: this.getPCLSAmount(value?.Amount, availableLTA),
                    Currency: CurrencyCode.GBP,
                },
            });
        }

        if (modelData.PensionTransfers && modelData.PensionTransfers.length > 0) {
            modelData.PensionTransfers.filter(
                (ele) =>
                    ele.PensionSource == PensionArrangementType.Accumulation && ele.TransferType == TransferType.Cash
            ).forEach((transfer, index) => {
                const value = transfer.CurrentValue;
                contributionRow.push({
                    Contribution: `Transfer ${index + 1}`,
                    CrystallisationAmount: value,
                    LTA: this.getLTACalculation(value?.Amount, totalValue, LTA),
                    TaxFreeLumpSum: {
                        Amount: this.getPCLSAmount(value?.Amount, availableLTA),
                        Currency: CurrencyCode.GBP,
                    },
                });
            });
        }
        return contributionRow;
    };

    public render(): JSX.Element {
        const { data, modelData } = this.props;
        const tableData = this.getTableData();
        let total = tableData.reduce((x, y) => x + y.TaxFreeLumpSum.Amount, 0);
        total = total > data.CurrentPclsEntitlement.Amount ? data.CurrentPclsEntitlement.Amount : total;
        return (
            <>
                {SectionHeader("Crystallisation per contribution")}
                <Alert
                    alertType={AlertType.Warning}
                    message={"The values shown here may change based on the contribution amounts we receive."}
                />
                <Table
                    data={tableData}
                    col={[
                        {
                            title: "Contribution",
                            key: "Contribution",
                        },
                        {
                            title: "Crystallisation amount",
                            key: "CrystallisationAmount",
                            render: (data, row, index) => {
                                return MoneyDisplayHelper.getMoneyFormat(data);
                            },
                        },
                        {
                            title: "LTA %",
                            key: "LTA",
                            render: (data, row: ChooseAssetsToSellAssets, index) => {
                                return data.toFixed(2) + "%";
                            },
                        },
                        {
                            thCssWrapper: "cell-right pr-0",
                            tdCssWrapper: "cell-right pr-0",
                            title: "Tax-free lump sum",
                            key: "TaxFreeLumpSum",
                            render: (data, row: ChooseAssetsToSellAssets, index) => {
                                return MoneyDisplayHelper.getMoneyFormat(data);
                            },
                        },
                    ]}
                    TotalBody={[
                        { data: "Total" },
                        {
                            data: (
                                <span>
                                    {MoneyDisplayHelper.getMoneyFormat({
                                        Amount:
                                            PensionHelper.singlePaymentsTotal(modelData) +
                                            PensionHelper.getTransfersTotal(modelData.PensionTransfers || []),
                                        Currency: CurrencyCode.GBP,
                                    })}
                                </span>
                            ),
                        },
                        {
                            data: <span>{data?.LifetimeAllowance?.SlaUsedByProposedCrystallisation || 0}%</span>,
                        },
                        {
                            data: (
                                <span>
                                    {MoneyDisplayHelper.getMoneyFormat({ Amount: total, Currency: CurrencyCode.GBP })}
                                </span>
                            ),
                            tfTdCssWrapper: "cell-right pr-0",
                        },
                    ]}
                />
            </>
        );
    }
}
